/**
 * Email Sidebar Component
 *
 * Displays the list of email conversations with search and filter functionality.
 */

import React from "react";
import { Search, Camera } from "lucide-react";
import { EmailSidebarProps } from "./types";

interface EmailConvoItemProps {
  conversation: {
    id: string;
    subject: string;
    updatedAt: string;
    unreadCount: number;
    participants: { name?: string; email: string }[];
    lastMessage?: { attachments?: { id: string }[] };
  };
  selected: boolean;
  onClick: () => void;
}

const EmailConvoItem: React.FC<EmailConvoItemProps> = ({
  conversation,
  selected,
  onClick,
}) => {
  const sender = conversation.participants.find(
    (p) => p.email !== "support@convoai.com"
  );
  const initials =
    sender?.name
      ?.split(" ")
      .map((n) => n[0])
      .join("")
      .toUpperCase()
      .slice(0, 2) || "??";

  const formatTime = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffDays = Math.floor(
      (now.getTime() - date.getTime()) / (1000 * 60 * 60 * 24)
    );

    if (diffDays === 0) {
      return date.toLocaleTimeString([], {
        hour: "2-digit",
        minute: "2-digit",
        hour12: true,
      }).toLowerCase();
    } else if (diffDays === 1) {
      return "Yesterday";
    } else if (diffDays < 7) {
      return date.toLocaleDateString([], { weekday: "short" });
    } else {
      return date.toLocaleDateString([], { month: "short", day: "numeric" });
    }
  };

  const hasAttachment =
    conversation.lastMessage?.attachments &&
    conversation.lastMessage.attachments.length > 0;

  return (
    <div
      onClick={onClick}
      className={`px-4 py-3.5 border-b border-gray-100 dark:border-gray-800 cursor-pointer transition-colors ${
        selected ? "bg-[#f8f5ff] dark:bg-indigo-500/10 border-l-[3px] border-l-[#7C83FD] pl-[13px]" : "bg-white dark:bg-gray-900 hover:bg-gray-50 dark:hover:bg-gray-800"
      }`}
    >
      <div className="flex items-center gap-3">
        {/* Avatar */}
        <div className="w-[46px] h-[46px] rounded-full bg-[#7C83FD] flex items-center justify-center text-white text-[14px] font-medium flex-shrink-0">
          {initials}
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0">
          {/* Name and Time Row */}
          <div className="flex items-center justify-between mb-0.5">
            <span className="text-[15px] font-semibold text-gray-900 dark:text-gray-100">
              {sender?.name || "Unknown"}
            </span>
            <span className="text-[13px] text-[#7B7B7B] font-semibold dark:text-gray-500 flex-shrink-0">
              {formatTime(conversation.updatedAt)}
            </span>
          </div>

          {/* Subject Row */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-1.5 flex-1 min-w-0">
              {hasAttachment && (
                <Camera size={13} className="text-[#7B7B7B] font-semibold dark:text-gray-500 flex-shrink-0" />
              )}
              <span className="text-[13px] text-gray-500 dark:text-gray-400 truncate">
                Re: {conversation.subject}
              </span>
            </div>
            {conversation.unreadCount > 0 && (
              <span className="ml-2 flex-shrink-0 bg-[#22c55e] text-white text-[11px] font-semibold rounded-full min-w-[20px] h-[20px] flex items-center justify-center">
                {conversation.unreadCount}
              </span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

interface FilterTabProps {
  label: string;
  count: number;
  active: boolean;
  onClick: () => void;
}

const FilterTab: React.FC<FilterTabProps> = ({
  label,
  count,
  active,
  onClick,
}) => (
  <button
    onClick={onClick}
    className="flex items-center gap-1 bg-transparent border-none cursor-pointer p-0"
  >
    <span
      className={`text-[13px] font-medium ${
        active ? "text-gray-700 dark:text-gray-200" : "text-[#7B7B7B] font-semibold dark:text-gray-500"
      }`}
    >
      {label}
    </span>
    <span
      className={`min-w-[18px] h-[18px] rounded-full text-[11px] flex items-center justify-center font-semibold ${
        active ? "bg-[#7C83FD] text-white" : "bg-gray-200 dark:bg-gray-700 text-gray-500 dark:text-gray-400"
      }`}
    >
      {count}
    </span>
  </button>
);

export const EmailSidebar: React.FC<EmailSidebarProps> = ({
  conversations,
  selectedConversation,
  onSelectConversation,
  loading = false,
  searchQuery,
  onSearchChange,
  activeTab,
  onTabChange,
}) => {
  const filtered = conversations.filter(
    (c) =>
      c.subject?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      c.participants.some((p) =>
        p.name?.toLowerCase().includes(searchQuery.toLowerCase())
      ) ||
      c.participants.some((p) =>
        p.email?.toLowerCase().includes(searchQuery.toLowerCase())
      )
  );

  const getFilteredByStatus = () => {
    if (activeTab === 0) return filtered;
    if (activeTab === 1) return filtered.filter((c) => c.status === "open");
    if (activeTab === 2) return filtered.filter((c) => c.status === "pending");
    return filtered;
  };

  const displayConversations = getFilteredByStatus();

  const allCount = conversations.length;
  const newCount = conversations.filter((c) => c.status === "open").length;
  const awaitingReplyCount = conversations.filter(
    (c) => c.status === "pending"
  ).length;

  return (
    <div className="w-[320px] min-w-[280px] bg-white dark:bg-gray-900 border-r border-gray-200 dark:border-gray-800 flex flex-col h-full overflow-hidden">
      {/* Search */}
      <div className="px-4 pt-4 pb-3 bg-[#f5f6fa] dark:bg-gray-800/60">
        <div className="flex items-center justify-between bg-white dark:bg-gray-800 rounded-md px-3 py-2.5 border border-[#7C83FD] dark:border-indigo-400">
          <input
            value={searchQuery}
            onChange={(e) => onSearchChange(e.target.value)}
            placeholder="Search Conversations"
            className="border-none bg-transparent outline-none text-[13px] text-gray-700 dark:text-gray-200 w-full placeholder:text-gray-500 dark:placeholder:text-gray-500"
          />
          <Search size={18} className="text-[#7C83FD] flex-shrink-0" />
        </div>
      </div>

      {/* Filter Tabs */}
      <div className="px-4 py-3 flex items-center gap-4 bg-[#f5f6fa] dark:bg-gray-800/60">
        <FilterTab
          label="All"
          count={allCount}
          active={activeTab === 0}
          onClick={() => onTabChange(0)}
        />
        <FilterTab
          label="New"
          count={newCount}
          active={activeTab === 1}
          onClick={() => onTabChange(1)}
        />
        <FilterTab
          label="Awaiting Reply"
          count={awaitingReplyCount}
          active={activeTab === 2}
          onClick={() => onTabChange(2)}
        />
      </div>

      {/* Conversation List */}
      <div className="flex-1 overflow-y-auto">
        {loading ? (
          <div className="flex justify-center items-center h-32">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#7C83FD]"></div>
          </div>
        ) : displayConversations.length === 0 ? (
          <div className="text-center text-[#7B7B7B] font-semibold dark:text-gray-500 text-sm py-8">
            <Search size={24} className="mx-auto mb-2" />
            <p>No emails found</p>
          </div>
        ) : (
          displayConversations.map((conversation) => (
            <EmailConvoItem
              key={conversation.id}
              conversation={conversation}
              selected={selectedConversation?.id === conversation.id}
              onClick={() => onSelectConversation(conversation)}
            />
          ))
        )}
      </div>
    </div>
  );
};
